import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
	Users,
	Search,
	Edit3,
	Eye,
	Phone,
	MapPin,
	Calendar,
	Shield,
	AlertTriangle,
	CheckCircle,
	XCircle,
	Baby,
	Star,
	DollarSign,
	Clock,
	Save,
	Activity,
} from "lucide-react";
import styles from './Admin.module.css';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface User {
	id: string;
	auth_user_id: string;
	email: string;
	full_name: string;
	role: "parent" | "doula";
	phone: string | null;
	location: string | null;
	created_at: string;
	last_active: string | null;
	onboarding_completed: boolean;
	onboarding_step: string | null;
	timezone: string | null;
	last_login: string | null;
	profile_complete: boolean;
	avatar_url?: string;
	// Doula specific fields
	listing_status?: "pending" | "active" | "suspended";
	years_experience?: number;
	hourly_rate?: number;
	// Parent specific fields
	total_bookings?: number;
	due_date?: string;
	birth_location?: string;
}

export default function UserManagement() {
	const [users, setUsers] = useState<User[]>([]);
	const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [activeTab, setActiveTab] = useState("parents");
	const [viewModalOpen, setViewModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState<User | null>(null);
	const { user } = useAuth();

	useEffect(() => {
		loadUsers();
	}, []);

	useEffect(() => {
		filterUsers();
	}, [users, searchTerm, statusFilter, activeTab]);

	const loadUsers = async () => {
		try {
			setLoading(true);

			if (!user) {
				throw new Error("User not authenticated");
			}

			// Call get-all-users edge function
			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/get-all-users`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						filter: {},
						pagination: {
							page: 1,
							limit: 1000, // Get all users for now
						},
					}),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to fetch users");
			}

			// Process the data to flatten related info
			const processedUsers =
				result.data.users?.map((u) => ({
					...u,
					listing_status: u.doula_listings?.[0]?.status,
					years_experience: u.doula_listings?.[0]?.years_experience,
					hourly_rate: u.doula_listings?.[0]?.hourly_rate,
					due_date: u.parent_profiles?.[0]?.due_date,
					birth_location: u.parent_profiles?.[0]?.birth_location,
					total_bookings: 0, // TODO: Calculate from appointments table
				})) || [];

			setUsers(processedUsers);
		} catch (error) {
			setUsers([]);
		} finally {
			setLoading(false);
		}
	};

	const filterUsers = () => {
		let filtered = users;

		// Filter by role based on active tab
		filtered = filtered.filter((user) => user.role === activeTab.slice(0, -1)); // Remove 's' from 'parents'/'doulas'

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(
				(user) =>
					user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					user.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					user.birth_location?.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}

		// Filter by status
		if (statusFilter !== "all") {
			filtered = filtered.filter((user) => {
				switch (statusFilter) {
					case "completed":
						return user.onboarding_completed;
					case "pending":
						return !user.onboarding_completed;
					case "listing-pending":
						return user.role === "doula" && user.listing_status === "pending";
					case "listing-active":
						return user.role === "doula" && user.listing_status === "active";
					case "listing-suspended":
						return user.role === "doula" && user.listing_status === "suspended";
					default:
						return true;
				}
			});
		}

		setFilteredUsers(filtered);
	};

	const handleViewUser = (user: User) => {
		setSelectedUser(user);
		setViewModalOpen(true);
	};

	const handleEditUser = (user: User) => {
		setSelectedUser(user);
		setEditModalOpen(true);
	};

	const handleSaveEdit = async () => {
		if (!selectedUser) return;

		try {
			// Get form values
			const fullName = (
				document.getElementById("edit-full-name") as HTMLInputElement
			)?.value;
			const phone = (document.getElementById("edit-phone") as HTMLInputElement)
				?.value;
			const dueDate = (
				document.getElementById("edit-due-date") as HTMLInputElement
			)?.value;
			const birthLocation = (
				document.getElementById("edit-birth-location") as HTMLInputElement
			)?.value;

			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			// Prepare user data updates
			const userUpdates: any = {};
			if (fullName !== selectedUser.full_name) userUpdates.full_name = fullName;
			if (phone !== selectedUser.phone) userUpdates.phone = phone;

			// Prepare parent profile updates (only for parents)
			const parentProfileUpdates: any = {};
			if (selectedUser.role === "parent") {
				if (dueDate !== selectedUser.due_date)
					parentProfileUpdates.due_date = dueDate;
				if (birthLocation !== selectedUser.birth_location)
					parentProfileUpdates.birth_location = birthLocation;
			}

			// Use the appropriate edge function based on role
			if (
				selectedUser.role === "parent" &&
				(Object.keys(userUpdates).length > 0 ||
					Object.keys(parentProfileUpdates).length > 0)
			) {
				const response = await fetch(
					`${supabase.supabaseUrl}/functions/v1/update-parent-profile`,
					{
						method: "POST",
						headers: {
							Authorization: `Bearer ${session.session.access_token}`,
							"Content-Type": "application/json",
						},
						body: JSON.stringify({
							user_id: selectedUser.id,
							user_data: userUpdates,
							parent_profile_data: parentProfileUpdates,
						}),
					},
				);

				if (!response.ok) {
					const errorText = await response.text();
					throw new Error(`HTTP ${response.status}: ${errorText}`);
				}

				const result = await response.json();

				if (!result.success) {
					throw new Error(result.error || "Failed to update parent profile");
				}
			} else if (Object.keys(userUpdates).length > 0) {
				// For non-parents or when only user data needs updating, use the regular update function
				// TODO: Create generic update-user edge function for non-parent updates
				// Generic user update functionality would go here
			}

			// Update local state
			setUsers((prev) =>
				prev.map((u) =>
					u.id === selectedUser.id
						? {
							...u,
							...userUpdates,
							...(selectedUser.role === "parent" ? parentProfileUpdates : {}),
						}
						: u,
				),
			);

			// Close modal
			setEditModalOpen(false);

			// Reload data to get fresh info
			loadUsers();
		} catch (error) {
			// TODO: Show error toast
		}
	};

	const getStatusBadge = (user: User) => {
		if (!user.onboarding_completed) {
			return (
				<Badge
					variant="secondary"
					className="flex items-center gap-1 bg-yellow-100 text-yellow-800 border-yellow-200"
				>
					<AlertTriangle className="w-3 h-3" />
					Setup Incomplete
				</Badge>
			);
		}

		if (user.role === "doula" && user.listing_status) {
			switch (user.listing_status) {
				case "pending":
					return (
						<Badge
							variant="secondary"
							className="flex items-center gap-1 bg-yellow-100 text-yellow-800 border-yellow-200"
						>
							<Clock className="w-3 h-3" />
							Listing Pending
						</Badge>
					);
				case "active":
					return (
						<Badge
							variant="default"
							className="flex items-center gap-1 bg-green-100 text-green-800 border-green-200"
						>
							<CheckCircle className="w-3 h-3" />
							Active Listing
						</Badge>
					);
				case "suspended":
					return (
						<Badge variant="destructive" className="flex items-center gap-1">
							<XCircle className="w-3 h-3" />
							Listing Suspended
						</Badge>
					);
			}
		}

		return (
			<Badge
				variant="default"
				className="flex items-center gap-1 bg-green-100 text-green-800 border-green-200"
			>
				<CheckCircle className="w-3 h-3" />
				Complete
			</Badge>
		);
	};

	const getFilterOptions = () => {
		const baseOptions = [
			{ value: "all", label: "All Users" },
			{ value: "completed", label: "Setup Complete" },
			{ value: "pending", label: "Setup Incomplete" },
		];

		if (activeTab === "doulas") {
			return [
				...baseOptions,
				{ value: "listing-pending", label: "Listing Pending" },
				{ value: "listing-active", label: "Listing Active" },
				{ value: "listing-suspended", label: "Listing Suspended" },
			];
		}

		return baseOptions;
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getWeeksPregnant = (dueDate: string) => {
		const due = new Date(dueDate);
		const today = new Date();
		const weeksDiff = Math.floor(
			(due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 7),
		);
		return Math.max(0, Math.min(40, 40 - weeksDiff));
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">Loading users...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Tabs */}
				<Tabs
					defaultValue="parents"
					value={activeTab}
					onValueChange={setActiveTab}
					className="space-y-6"
				>
					<div className="flex justify-center">
						<TabsList className={`${styles.tabButtons} grid w-fit grid-cols-2 border border-newTheme-lightGrey h-auto`}>
							<TabsTrigger
								value="parents"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Baby className="w-4 h-4" />
								Parent Users ({users.filter((u) => u.role === "parent").length})
							</TabsTrigger>
							<TabsTrigger
								value="doulas"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Shield className="w-4 h-4" />
								Doula Users ({users.filter((u) => u.role === "doula").length})
							</TabsTrigger>
						</TabsList>
					</div>

					<TabsContent value={activeTab} className="space-y-6">
						{/* Search and Filters */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="glass-card border-0 shadow-lg p-6 rounded-2xl"
						>
							<div className="flex flex-col lg:flex-row gap-4">
								{/* Search */}
								<div className="relative flex-1">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
									<Input
										type="search"
										placeholder={`Search ${activeTab} by name, email, or location...`}
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className={`${styles.adminInput} font-karla text-base h-auto shadow-btn-shadow pl-10 py-[12px] bg-white border-newTheme-lightGrey rounded-md`}
									/>
								</div>

								{/* Status Filter */}
								<div className="lg:w-64 lg:text-right">
									<select onChange={() => setStatusFilter} className="h-12 text-lg bg-white/80 backdrop-blur-sm font-karla border border-newTheme-lightGrey rounded-md p-2">
										{getFilterOptions().map((option) => (
											<option key={option.value} value={option.value}>
												{option.label}
											</option>
										))}

									</select>
								</div>
							</div>

							{/* Stats */}
							<div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
								<div className="text-center">
									<div className="text-2xl font-bold text-sage-600">
										{
											users.filter((u) => u.role === activeTab.slice(0, -1))
												.length
										}
									</div>
									<div className="text-sm text-black font-karla">
										Total {activeTab}
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-spruce">
										{
											users.filter(
												(u) =>
													u.role === activeTab.slice(0, -1) &&
													u.onboarding_completed,
											).length
										}
									</div>
									<div className="text-sm text-black font-karla">
										Setup Complete
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-orange">
										{
											users.filter(
												(u) =>
													u.role === activeTab.slice(0, -1) &&
													!u.onboarding_completed,
											).length
										}
									</div>
									<div className="text-sm text-black font-karla">
										Setup Incomplete
									</div>
								</div>
								{activeTab === "doulas" && (
									<div className="text-center">
										<div className="text-2xl font-bold text-newTheme-lime">
											{
												users.filter(
													(u) =>
														u.role === "doula" && u.listing_status === "active",
												).length
											}
										</div>
										<div className="text-sm text-black font-karla">
											Active Listings
										</div>
									</div>
								)}
								{activeTab === "parents" && (
									<div className="text-center">
										<div className="text-2xl font-bold text-newTheme-grey">
											{
												users.filter((u) => u.role === "parent" && u.due_date)
													.length
											}
										</div>
										<div className="text-sm text-black font-karla">
											With Due Date
										</div>
									</div>
								)}
							</div>
						</motion.div>

						{/* Users Grid */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
						>
							{filteredUsers.length === 0 ? (
								<div className="col-span-full">
									<Card className="glass-card border-0 shadow-lg">
										<CardContent className="p-12 text-center">
											<Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
											<h3 className="text-xl font-semibold text-gray-900 mb-2 font-karla">
												No {activeTab} Found
											</h3>
											<p className="text-black font-karla">
												{searchTerm
													? `No ${activeTab} match "${searchTerm}". Try adjusting your search terms.`
													: `No ${activeTab} match the selected filters.`}
											</p>
										</CardContent>
									</Card>
								</div>
							) : (
								filteredUsers.map((user, index) => (
									<motion.div
										key={user.id}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.6, delay: 0.1 + index * 0.05 }}
									>
										<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
											<CardHeader className="pb-4">
												<div className="flex items-start justify-between">
													<div className="flex items-center space-x-3 flex-1 min-w-0">
														{/* Profile Photo */}
														<div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0 border-2 border-primary-200">
															{user.avatar_url ? (
																<img
																	src={user.avatar_url}
																	alt={user.full_name}
																	className="w-full h-full object-cover"
																/>
															) : (
																<div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
																	{user.role === "doula" ? (
																		<Shield className="h-6 w-6 text-primary-600" />
																	) : (
																		<Baby className="h-6 w-6 text-primary-600" />
																	)}
																</div>
															)}
														</div>
														<div className="flex-1 min-w-0">
															{/* Name and Status */}
															<h3 className="text-lg font-bold text-gray-900 truncate font-karla">
																{user.full_name || "Name not set"}
															</h3>
															<p className="text-sm text-gray-600 mb-1 truncate font-karla">
																{user.email}
															</p>
															{user.phone && (
																<p className="text-xs text-gray-500 truncate font-karla">
																	{user.phone}
																</p>
															)}
														</div>
													</div>
													{getStatusBadge(user)}
												</div>
											</CardHeader>

											<CardContent className="space-y-4">
												{/* User Details */}
												<div className="space-y-3">
													<div className="flex items-center text-sm text-gray-600">
														<Calendar className="h-4 w-4 mr-2 text-primary-600" />
														<span className="font-karla">
															Joined {formatDate(user.created_at)}
														</span>
													</div>

													{user.location && (
														<div className="flex items-center text-sm text-gray-600">
															<MapPin className="h-4 w-4 mr-2 text-primary-600" />
															<span className="font-karla truncate">
																{user.location}
															</span>
														</div>
													)}

													{/* Role-specific information */}
													{user.role === "doula" && (
														<>
															{user.years_experience && (
																<div className="flex items-center text-sm text-gray-600">
																	<Star className="h-4 w-4 mr-2 text-primary-600" />
																	<span className="font-karla">
																		{user.years_experience} years experience
																	</span>
																</div>
															)}
															{user.hourly_rate && (
																<div className="flex items-center text-sm text-gray-600">
																	<DollarSign className="h-4 w-4 mr-2 text-primary-600" />
																	<span className="font-karla">
																		${user.hourly_rate}/hour
																	</span>
																</div>
															)}
														</>
													)}

													{user.role === "parent" && (
														<>
															{user.due_date && (
																<div className="flex items-center text-sm text-gray-600">
																	<Baby className="h-4 w-4 mr-2 text-primary-600" />
																	<span className="font-karla">
																		Due {formatDate(user.due_date)} •{" "}
																		{getWeeksPregnant(user.due_date)} weeks
																	</span>
																</div>
															)}
															{user.birth_location && (
																<div className="flex items-center text-sm text-gray-600">
																	<MapPin className="h-4 w-4 mr-2 text-primary-600" />
																	<span className="font-karla truncate">
																		Birth: {user.birth_location}
																	</span>
																</div>
															)}
														</>
													)}
												</div>

												{/* Actions */}
												<div className="flex items-center justify-center pt-4 border-t border-gray-100">
													<div className="flex space-x-2">
														<Button
															size="sm"
															variant="outline"
															className={`${styles.primaryBtn} border border-newTheme-lightGrey`}
															onClick={() => handleViewUser(user)}
														>
															<Eye className="w-3 h-3 mr-1" />
															View
														</Button>
														<Button
															size="sm"
															variant="outline"
															className={`${styles.primaryBtn} border border-newTheme-lightGrey`}
															onClick={() => handleEditUser(user)}
														>
															<Edit3 className="w-3 h-3 mr-1" />
															Edit
														</Button>
													</div>
												</div>
											</CardContent>
										</Card>
									</motion.div>
								))
							)}
						</motion.div>
					</TabsContent>
				</Tabs>

				{/* View User Modal */}
				<Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
					<DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto glass-card border-0 shadow-2xl bg-newTheme-cotton">
						<DialogHeader className="border-b border-gray-200 pb-4">
							<DialogTitle className="flex items-center gap-3 font-karla text-2xl text-gray-900">
								<div className="w-10 h-10 rounded-lg bg-sage-100 flex items-center justify-center">
									<Eye className="w-5 h-5 text-sage-600" />
								</div>
								User Details
							</DialogTitle>
						</DialogHeader>
						{selectedUser && (
							<div className="space-y-8 pt-6">
								{/* Basic Info Card */}
								<Card className="border-0 shadow-md bg-white">
									<CardHeader className="pb-3">
										<CardTitle className="text-lg font-karla text-gray-900 flex items-center gap-2">
											<Users className="w-5 h-5 text-sage-600" />
											Basic Information
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													User ID
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-mono">
														{selectedUser.id}
													</p>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Auth User ID
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-mono">
														{selectedUser.auth_user_id}
													</p>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Email
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-karla">
														{selectedUser.email}
													</p>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Full Name
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-karla">
														{selectedUser.full_name || "Not set"}
													</p>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Role
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-karla capitalize">
														{selectedUser.role}
													</p>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Phone
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-karla">
														{selectedUser.phone || "Not set"}
													</p>
												</div>
											</div>
										</div>
									</CardContent>
								</Card>

								{/* Status & Timeline Card */}
								<Card className="border-0 shadow-md bg-white">
									<CardHeader className="pb-3">
										<CardTitle className="text-lg font-karla text-gray-900 flex items-center gap-2">
											<Activity className="w-5 h-5 text-blue-600" />
											Status & Timeline
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Onboarding Status
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<div className="flex items-center gap-2">
														{selectedUser.onboarding_completed ? (
															<CheckCircle className="w-4 h-4 text-green-600" />
														) : (
															<AlertTriangle className="w-4 h-4 text-yellow-600" />
														)}
														<p className="text-sm text-gray-900 font-karla">
															{selectedUser.onboarding_completed
																? "Complete"
																: "Incomplete"}
														</p>
													</div>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Current Step
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-karla">
														{selectedUser.onboarding_step || "Not set"}
													</p>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Created At
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-karla">
														{formatDate(selectedUser.created_at)}
													</p>
												</div>
											</div>
											<div className="space-y-2">
												<Label className="text-sm font-semibold text-gray-700 font-karla">
													Last Login
												</Label>
												<div className="p-3 bg-white rounded-lg border border-gray-200">
													<p className="text-sm text-gray-900 font-karla">
														{selectedUser.last_login
															? formatDate(selectedUser.last_login)
															: "Never"}
													</p>
												</div>
											</div>
										</div>
									</CardContent>
								</Card>

								{/* Role-specific info */}
								{selectedUser.role === "doula" && (
									<Card className="border-0 shadow-md bg-white">
										<CardHeader className="pb-3">
											<CardTitle className="text-lg font-karla text-gray-900 flex items-center gap-2">
												<Shield className="w-5 h-5 text-purple-600" />
												Doula Information
											</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
												<div className="space-y-2">
													<Label className="text-sm font-semibold text-gray-700 font-karla">
														Listing Status
													</Label>
													<div className="p-3 bg-white rounded-lg border border-gray-200">
														<p className="text-sm text-gray-900 font-karla">
															{selectedUser.listing_status || "Not set"}
														</p>
													</div>
												</div>
												<div className="space-y-2">
													<Label className="text-sm font-semibold text-gray-700 font-karla">
														Years Experience
													</Label>
													<div className="p-3 bg-white rounded-lg border border-gray-200">
														<p className="text-sm text-gray-900 font-karla">
															{selectedUser.years_experience || "Not set"}
														</p>
													</div>
												</div>
												<div className="space-y-2">
													<Label className="text-sm font-semibold text-gray-700 font-karla">
														Hourly Rate
													</Label>
													<div className="p-3 bg-white rounded-lg border border-gray-200">
														<p className="text-sm text-gray-900 font-karla">
															{selectedUser.hourly_rate
																? `$${selectedUser.hourly_rate}`
																: "Not set"}
														</p>
													</div>
												</div>
											</div>
										</CardContent>
									</Card>
								)}

								{selectedUser.role === "parent" && (
									<Card className="border-0 shadow-md bg-white">
										<CardHeader className="pb-3">
											<CardTitle className="text-lg font-karla text-gray-900 flex items-center gap-2">
												<Baby className="w-5 h-5 text-green-600" />
												Parent Information
											</CardTitle>
										</CardHeader>
										<CardContent>
											<div className="grid grid-cols-2 gap-6">
												<div className="space-y-2">
													<Label className="text-sm font-semibold text-gray-700 font-karla">
														Due Date
													</Label>
													<div className="p-3 bg-white rounded-lg border border-gray-200">
														<p className="text-sm text-gray-900 font-karla">
															{selectedUser.due_date
																? formatDate(selectedUser.due_date)
																: "Not set"}
														</p>
													</div>
												</div>
												<div className="space-y-2">
													<Label className="text-sm font-semibold text-gray-700 font-karla">
														Birth Location
													</Label>
													<div className="p-3 bg-white rounded-lg border border-gray-200">
														<p className="text-sm text-gray-900 font-karla">
															{selectedUser.birth_location || "Not set"}
														</p>
													</div>
												</div>
											</div>
										</CardContent>
									</Card>
								)}
							</div>
						)}
					</DialogContent>
				</Dialog>

				{/* Edit User Modal */}
				<Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
					<DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto glass-card border-0 shadow-2xl bg-newTheme-cotton">
						<DialogHeader className="border-b border-gray-200 pb-4">
							<DialogTitle className="flex items-center gap-3 font-karla text-2xl text-gray-900">
								<div className="w-10 h-10 rounded-lg bg-sage-100 flex items-center justify-center">
									<Edit3 className="w-5 h-5 text-sage-600" />
								</div>
								Edit User Profile
							</DialogTitle>
						</DialogHeader>
						{selectedUser && (
							<div className="space-y-6 pt-6">
								{/* Basic Information Card */}
								<Card className="border-0 shadow-md bg-white">
									<CardHeader className="pb-3">
										<CardTitle className="text-lg font-karla text-gray-900 flex items-center gap-2">
											<Users className="w-5 h-5 text-sage-600" />
											Basic Information
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div className="space-y-2">
												<Label
													htmlFor="edit-full-name"
													className="text-sm font-semibold text-gray-700 font-karla"
												>
													Full Name
												</Label>
												<Input
													id="edit-full-name"
													defaultValue={selectedUser.full_name || ""}
													className="font-karla bg-white border-gray-300 focus:border-sage-500 focus:ring-sage-500"
													placeholder="Enter full name"
												/>
											</div>
											<div className="space-y-2">
												<Label
													htmlFor="edit-phone"
													className="text-sm font-semibold text-gray-700 font-karla"
												>
													Phone Number
												</Label>
												<div className="relative">
													<Input
														id="edit-phone"
														defaultValue={selectedUser.phone || ""}
														className="font-karla bg-white border-gray-300 focus:border-sage-500 focus:ring-sage-500 pl-8"
														placeholder="(555) 123-4567"
													/>
													<Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
												</div>
											</div>
										</div>
									</CardContent>
								</Card>

								{/* Parent-specific Information */}
								{selectedUser.role === "parent" && (
									<Card className="border-0 shadow-md bg-white">
										<CardHeader className="pb-3">
											<CardTitle className="text-lg font-karla text-gray-900 flex items-center gap-2">
												<Baby className="w-5 h-5 text-green-600" />
												Parent Information
											</CardTitle>
										</CardHeader>
										<CardContent className="space-y-4">
											<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
												<div className="space-y-2">
													<Label
														htmlFor="edit-due-date"
														className="text-sm font-semibold text-gray-700 font-karla"
													>
														Due Date
													</Label>
													<div className="relative">
														<Input
															id="edit-due-date"
															type="date"
															defaultValue={selectedUser.due_date || ""}
															className="font-karla bg-white border-gray-300 focus:border-green-500 focus:ring-green-500 pl-8"
														/>
														<Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
													</div>
												</div>
												<div className="space-y-2">
													<Label
														htmlFor="edit-birth-location"
														className="text-sm font-semibold text-gray-700 font-karla"
													>
														Birth Location
													</Label>
													<div className="relative">
														<Input
															id="edit-birth-location"
															defaultValue={selectedUser.birth_location || ""}
															className="font-karla bg-white border-gray-300 focus:border-green-500 focus:ring-green-500 pl-8"
															placeholder="Hospital or birth center name"
														/>
														<MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
													</div>
												</div>
											</div>
										</CardContent>
									</Card>
								)}

								{/* Action Buttons */}
								<div className="flex justify-between items-center pt-6 border-t border-gray-200">
									<div className="text-sm text-gray-500 font-karla">
										Note: Email address cannot be changed
									</div>
									<div className="flex space-x-3">
										<Button
											variant="outline"
											onClick={() => setEditModalOpen(false)}
											className="font-karla border-gray-300 hover:bg-gray-50"
										>
											Cancel
										</Button>
										<Button
											onClick={handleSaveEdit}
											className="bg-primary-500 hover:bg-primary-600 text-white font-karla shadow-lg"
										>
											<Save className="w-4 h-4 mr-2" />
											Save Changes
										</Button>
									</div>
								</div>
							</div>
						)}
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
}
